import React, { createContext, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  
  // Initialize with Arabic as default, check localStorage first
  const getInitialLanguage = () => {
    const stored = localStorage.getItem("preferredLanguage");
    // If user has explicitly chosen English or Arabic, respect that
    if (stored === "en" || stored === "ar") {
      return stored;
    }
    // Default to Arabic for new users
    return "ar";
  };
  
  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage());
  const [isRTL, setIsRTL] = useState(getInitialLanguage() === "ar");

  // RTL languages list
  const rtlLanguages = ["ar", "he", "fa", "ur"];

  // Sync with i18n on mount - ensure Arabic is set as default
  useEffect(() => {
    const initialLanguage = getInitialLanguage();
    // If i18n language doesn't match our initial language, change it
    if (i18n.language !== initialLanguage) {
      i18n.changeLanguage(initialLanguage);
    }
    // Ensure state is in sync
    if (currentLanguage !== initialLanguage) {
      setCurrentLanguage(initialLanguage);
    }
  }, []);

  // Update RTL state when language changes
  useEffect(() => {
    const isRightToLeft = rtlLanguages.includes(currentLanguage);
    setIsRTL(isRightToLeft);

    // Update HTML direction attribute
    document.documentElement.dir = isRightToLeft ? "rtl" : "ltr";
    document.documentElement.lang = currentLanguage;

    // Update body class for CSS styling
    document.body.classList.toggle("rtl", isRightToLeft);
    document.body.classList.toggle("ltr", !isRightToLeft);

    // Store language preference
    localStorage.setItem("preferredLanguage", currentLanguage);
  }, [currentLanguage]);

  // Change language function
  const changeLanguage = async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  // Get available languages
  const getAvailableLanguages = () => [
    {
      code: "en",
      name: "English",
      nativeName: "English",
      flag: "🇺🇸",
      isRTL: false,
    },
    {
      code: "ar",
      name: "Arabic",
      nativeName: "العربية",
      flag: "🇸🇦",
      isRTL: true,
    },
  ];

  // Get current language info
  const getCurrentLanguageInfo = () => {
    const languages = getAvailableLanguages();
    return (
      languages.find((lang) => lang.code === currentLanguage) || languages.find((lang) => lang.code === "ar") || languages[0]
    );
  };

  // Format text based on RTL/LTR
  const formatText = (text, options = {}) => {
    if (!text) return "";

    const { preserveNumbers = true, preserveEnglish = true } = options;

    let formattedText = text;

    // Handle number formatting for RTL languages
    if (isRTL && preserveNumbers) {
      // Keep numbers in LTR format even in RTL text
      formattedText = formattedText.replace(/(\d+)/g, "\u202D$1\u202C");
    }

    return formattedText;
  };

  // Get CSS direction classes
  const getDirectionClasses = () => ({
    text: isRTL ? "text-right" : "text-left",
    float: isRTL ? "float-right" : "float-left",
    marginStart: isRTL ? "ml-auto" : "mr-auto",
    marginEnd: isRTL ? "mr-auto" : "ml-auto",
    paddingStart: isRTL ? "pl-0" : "pr-0",
    paddingEnd: isRTL ? "pr-0" : "pl-0",
    borderStart: isRTL ? "border-left" : "border-right",
    borderEnd: isRTL ? "border-right" : "border-left",
  });

  const value = {
    currentLanguage,
    isRTL,
    changeLanguage,
    getAvailableLanguages,
    getCurrentLanguageInfo,
    formatText,
    getDirectionClasses,
    rtlLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
